/**
 * Store for managing members data
 *
 * IMPORTANT REACTIVITY NOTES:
 * 1. Always use $patch for state updates to ensure proper reactivity
 * 2. Cross-store dependencies are managed through subscriptions
 * 3. Captain assignment updates both stores at the point of mutation —
 *    see `assignCaptainToSubKrewe` / `removeCaptainFromSubKrewe`, with
 *    `ensureCaptainSynchronized` as the reconciliation path
 * 4. Captain and SubKrewe relationships are maintained bidirectionally
 */

import type { DocumentReference } from 'firebase/firestore'
import { addDoc, arrayUnion, collection, deleteDoc, deleteField, doc, getDoc, getDocs, onSnapshot, orderBy, query, serverTimestamp, updateDoc, where } from 'firebase/firestore'
import { defineStore } from 'pinia'
import { useCurrentUser } from 'vuefire'
import { useMembershipNotifications } from '~/composables/notifications/useMembershipNotifications.js'
import type { LiveChange, LiveSpec } from '~/utils/liveData'
import { acquireLive, holdLive, liveDocListener, liveQueryListener } from '~/utils/liveData'
import type { Member, MemberFirestoreData } from '~/shared/types/member.js'
import { deepTrim } from '~/shared/utils/sanitize.js'
import { toDate } from '~/shared/utils/dates.js'
import { buildApprovalUpdate, isPerYearApprovalSubkrewe } from '~/shared/utils/subkreweApproval.js'
import type { ApprovalScope, ApprovalStatus } from '~/shared/utils/subkreweApproval.js'
import { duesConfigsWithDates } from '~/shared/utils/subkreweDues.js'
import { SUBKREWE_LINK_FIELDS, pickSubKreweLinkFields } from '~/shared/utils/subkreweLinks.js'

// Each live roster: SubKrewe id → its members' ids (rosterSpec)
const rosters = new Map<string, Set<string>>()

export const useSubKrewesStore = defineStore('subkrewes', {
  state: () => ({
    // Store subkrewes by document path for O(1) lookups
    subkrewes: {} as Record<string, SubKrewe>,
    loading: false,
    initialized: false,
    subkreweMembersLoaded: false,
    // The browsable SubKrewes (subscribeToAvailableSubKrewes) have arrived
    availableLoaded: false,
  }),

  getters: {
    // Convert map to array for components that expect arrays
    subkrewesArray(): SubKrewe[] {
      return Object.values(this.subkrewes)
    },

    // Get a subkrewe by ID
    getSubkreweById(): (id: string) => SubKrewe | undefined {
      return (id: string) => {
        const path = `subkrewes/${id}`
        return this.subkrewes[path]
      }
    },

    // Get members of a specific subkrewe
    getMembersForSubKrewe(): (subkreweId: string) => Member[] {
      return (subkreweId: string) => {
        const path = `subkrewes/${subkreweId}`
        return this.subkrewes[path]?.members || []
      }
    },

    availableSubkrewes(): SubKrewe[] {
      return this.subkrewesArray.filter(sk =>
        !sk.captains || sk.captains.length === 0,
      )
    },
  },

  actions: {
    /**
     * Convert Firestore document data to a SubKrewe object
     * With streamlined captain resolution
     */
    convertToSubKrewe(docId: string, data: SubKreweFirestoreData, resolveCaptains = true): SubKrewe {
      // Handle date conversions
      const created_at = data.created_at ? data.created_at : new Date()
      const updated_at = data.updated_at ? data.updated_at : new Date()

      // Create the basic subkrewe
      const subkrewe: SubKrewe = {
        id: docId,
        name: data.name,
        description: data.description,
        captains: [], // Initialize as empty array
        members: [], // Initialize empty members array
        approved: data.approved,
        approved_at: data.approved_at,
        approved_by: data.approved_by,
        approval_changed_at: data.approval_changed_at,
        approval_changed_by: data.approval_changed_by,
        approval_by_year: data.approval_by_year,
        created_at,
        updated_at,
        // Other fields remain the same
        estimated_members: ensureNumber(data.estimated_members),
        expected: data.expected, // Using new expected structure
        admission_policy: data.admission_policy,
        admission_restrictions: data.admission_restrictions,
        contraption: data.contraption,
        contraption_info: data.contraption_info,
        contraption_count: data.contraption_count,
        music: data.music,
        music_other: data.music_other,
        rolling_next_parade: data.rolling_next_parade,
        rolling_next_parade_by_year: data.rolling_next_parade_by_year,
        declined_by: data.declined_by,
        decline_reason: data.decline_reason,
        email: data.email, // Add the email field
        abandoned: data.abandoned,
        // A Timestamp from Firestore; null (left out) while serverTimestamp() is pending
        abandoned_at: toDate(data.abandoned_at) ?? undefined,
        abandoned_by: data.abandoned_by,
        abandonment_reason: data.abandonment_reason,
        visible_in_browser: data.visible_in_browser,
        sponsored: data.sponsored,
        specialty: data.specialty || [], // Map specialty from Firestore
        nerd_herder_cap: data.nerd_herder_cap,
        discount_type: data.discount_type,
        discount_percent: data.discount_percent,
        discount_fixed_price: data.discount_fixed_price,
        discount_cap: data.discount_cap,
        discount_consumed_by_year: data.discount_consumed_by_year,
        terms_and_conditions: data.terms_and_conditions,
        terms_updated_at: data.terms_updated_at instanceof Date
          ? data.terms_updated_at
          : (data.terms_updated_at && typeof (data.terms_updated_at as { toDate?: () => Date }).toDate === 'function'
            ? (data.terms_updated_at as unknown as { toDate: () => Date }).toDate()
            : (data.terms_updated_at as Date | undefined)),
        subkrewe_dues_by_year: duesConfigsWithDates(data.subkrewe_dues_by_year),
        directory_enabled: data.directory_enabled,
        social_media: data.social_media,
      }

      // Website / social media links, driven off the platform registry so
      // adding a platform doesn't require touching this mapping.
      for (const field of SUBKREWE_LINK_FIELDS) {
        subkrewe[field] = data[field]
      }

      // If no captains reference or not resolving, return as is
      if (!data.captains || !resolveCaptains) {
        return subkrewe
      }

      // Get the members store once
      const membersStore = useMembersStore()

      // Resolve captains - fetch each captain by ID
      if (Array.isArray(data.captains)) {
        // For each captain reference, fetch the actual member
        data.captains.forEach((captainRef) => {
          if (!captainRef || typeof captainRef !== 'object' || !('id' in captainRef) || typeof captainRef.id !== 'string') {
            console.warn('[SubKrewes] Skipping invalid captain reference', captainRef)
            return
          }
          const captainId = captainRef.id as string
          if (!captainId) {
            console.warn('[SubKrewes] Empty captain id in reference, skipping')
            return
          }

          const subkrewePath = `subkrewes/${subkrewe.id}`
          // Try to resolve synchronously from members store (for tests and already-loaded members)
          const existingCaptain = membersStore.members[`members/${captainId}`]
          if (existingCaptain) {
            existingCaptain.subkrewe = subkrewe
            // Add to returned subkrewe object immediately
            if (!subkrewe.captains.some(c => c.id === existingCaptain.id)) {
              subkrewe.captains.push(existingCaptain)
            }
            // Also patch stored subkrewe if present
            const storedSubkrewe = this.subkrewes[subkrewePath]
            if (storedSubkrewe && !storedSubkrewe.captains.some(c => c.id === existingCaptain.id)) {
              this.$patch((state) => {
                if (state.subkrewes[subkrewePath]) {
                  state.subkrewes[subkrewePath].captains.push(existingCaptain)
                }
              })
            }
            return
          }

          // Otherwise fetch the captain asynchronously
          membersStore.fetchCaptain(captainId).then((captain) => {
            if (captain) {
              captain.subkrewe = subkrewe
              const storedSubkrewe = this.subkrewes[subkrewePath]
              if (storedSubkrewe) {
                if (!storedSubkrewe.captains.some(c => c.id === captain.id)) {
                  this.$patch((state) => {
                    if (state.subkrewes[subkrewePath]) {
                      state.subkrewes[subkrewePath].captains.push(captain)
                    }
                  })
                }
              }
            }
          }).catch((error) => {
            console.error(`[SubKrewes] Error fetching captain ${captainId}:`, error)
          })
        })
      }

      return subkrewe
    },

    /**
     * Convert a SubKrewe object to Firestore-compatible data format
     * No change needed here since member references are managed separately
     */
    convertFirestoreData(subkrewe: SubKrewe): Partial<SubKreweFirestoreData> {
      const db = useFirestore()

      // Create the base firestore data object
      const firestoreData: Partial<SubKreweFirestoreData> = {
        // Basic fields
        name: subkrewe.name,
        description: subkrewe.description,
        updated_at: serverTimestamp(),

        // Additional fields
        approved: subkrewe.approved,
        estimated_members: ensureNumber(subkrewe.estimated_members),
        expected: subkrewe.expected, // Using new expected structure
        admission_policy: subkrewe.admission_policy,
        admission_restrictions: subkrewe.admission_restrictions,
        contraption: subkrewe.contraption,
        contraption_info: subkrewe.contraption_info,
        music: subkrewe.music,
        music_other: subkrewe.music_other,
        rolling_next_parade: subkrewe.rolling_next_parade,
        rolling_next_parade_by_year: subkrewe.rolling_next_parade_by_year,
        declined_by: subkrewe.declined_by,
        decline_reason: subkrewe.decline_reason,
        email: subkrewe.email, // Add the email field
        abandoned: subkrewe.abandoned,
        abandoned_at: subkrewe.abandoned_at,
        abandoned_by: subkrewe.abandoned_by,
        abandonment_reason: subkrewe.abandonment_reason,
        visible_in_browser: subkrewe.visible_in_browser,
        sponsored: subkrewe.sponsored,
        nerd_herder_cap: subkrewe.nerd_herder_cap,
        discount_type: subkrewe.discount_type,
        discount_percent: subkrewe.discount_percent,
        discount_fixed_price: subkrewe.discount_fixed_price,
        discount_cap: subkrewe.discount_cap,
        discount_consumed_by_year: subkrewe.discount_consumed_by_year,
        terms_and_conditions: subkrewe.terms_and_conditions,
        terms_updated_at: subkrewe.terms_updated_at,
        subkrewe_dues_by_year: subkrewe.subkrewe_dues_by_year,
        directory_enabled: subkrewe.directory_enabled,
      }

      // Remove any undefined fields, because Firestore doesn't like them.
      // Iterated through a Record view: the keys come from `for...in`, which
      // TypeScript widens to `string`, and so cannot index the typed shape.
      const writableFields = firestoreData as Record<string, unknown>
      for (const key in writableFields) {
        if (writableFields[key] === undefined) {
          writableFields[key] = deleteField()
        }
      }

      // Handle captains reference - convert array of objects to array of DocumentReferences
      if (subkrewe.captains && subkrewe.captains.length > 0) {
        firestoreData.captains = subkrewe.captains.map(captain =>
          doc(db, 'members', captain.id),
        )
      }
      else {
        firestoreData.captains = deleteField()
      }

      return firestoreData
    },

    /**
     * Ensure subkrewes are loaded
     */
    async ensureSubKrewesLoaded(): Promise<void> {
      await this.fetchSubKrewes()
    },

    /**
     * Every SubKrewe, live while the caller holds them (admins hold them from
     * sign-in, stores/adminLive.ts). With `loadMembers`, every member too, so
     * each SubKrewe carries its roster.
     */
    async fetchSubKrewes(loadMembers = false): Promise<SubKrewe[]> {
      const live = useAdminLiveStore()
      this.loading = !this.initialized
      try {
        await Promise.all([
          acquireLive(live.subkrewesSpec()),
          loadMembers ? acquireLive(live.membersSpec()) : Promise.resolve(),
        ])
        this.initialized = true
      }
      catch (error) {
        console.error('Error fetching subkrewes:', error)
      }
      finally {
        this.loading = false
      }
      return this.subkrewesArray
    },

    /**
     * Fetch subkrewes with members preloaded
     */
    async fetchSubKrewesWithMembers(): Promise<SubKrewe[]> {
      return await this.fetchSubKrewes(true)
    },

    /**
    * A SubKrewe from a DocumentReference (resolving references from other
    * documents), live while the caller holds it
    */
    async fetchSubKreweByRef(
      subkreweRef: DocumentReference | { path: string } | null | undefined,
    ): Promise<SubKrewe | null> {
      const match = subkreweRef && typeof subkreweRef.path === 'string'
        ? /^subkrewes\/([^/]+)$/.exec(subkreweRef.path.trim())
        : null
      if (!match) {
        if (subkreweRef) console.warn('[SubKrewes] Unsupported reference for fetchSubKreweByRef', subkreweRef)
        return null
      }
      return await this.fetchSubKrewe(match[1]!)
    },

    /**
     * A SubKrewe, live while the caller holds it (utils/liveData.ts)
     */
    async fetchSubKrewe(id: string): Promise<SubKrewe | null> {
      if (!id) return null
      try {
        await acquireLive(this.subkreweSpec(id))
      }
      catch (error) {
        console.error(`Error fetching subkrewe ${id}:`, error)
        return null
      }
      const subkrewe = this.subkrewes[`subkrewes/${id}`] ?? null
      if (!subkrewe) console.warn(`SubKrewe at path subkrewes/${id} does not exist`)
      return subkrewe
    },

    subkreweSpec(id: string): LiveSpec<SubKreweFirestoreData> {
      const path = `subkrewes/${id}`
      return {
        key: path,
        within: 'subkrewes',
        listen: (emit, fail) => onSnapshot(doc(useFirestore(), 'subkrewes', id), ...liveDocListener(path, emit, fail)),
        apply: changes => this.applySubkrewes(changes),
        evict: paths => this.evictSubkrewes(paths),
      }
    },

    availableSpec(): LiveSpec<SubKreweFirestoreData> {
      return {
        key: 'subkrewes?approved=Yes',
        within: 'subkrewes',
        listen: (emit, fail) => onSnapshot(
          query(collection(useFirestore(), 'subkrewes'), where('approved', '==', 'Yes'), orderBy('name', 'asc')),
          ...liveQueryListener('subkrewes', emit, (error) => {
            // Stop sketching the list; it shows what it has
            this.availableLoaded = true
            fail(error)
          }),
        ),
        apply: changes => this.applySubkrewes(changes),
        evict: paths => this.evictSubkrewes(paths),
        ready: () => {
          this.availableLoaded = true
        },
      }
    },

    /**
     * A SubKrewe's members, live: everyone whose record points at it (only
     * its captains and admins may read them, firestore.rules)
     */
    rosterSpec(subkreweId: string): LiveSpec<MemberFirestoreData> {
      const membersStore = useMembersStore()
      const subkrewePath = `subkrewes/${subkreweId}`
      const idOf = (path: string) => path.slice('members/'.length)
      return {
        key: `members?subkrewe=${subkreweId}`,
        within: 'members',
        listen: (emit, fail) => onSnapshot(
          query(collection(useFirestore(), 'members'), where('subkrewe', '==', doc(useFirestore(), 'subkrewes', subkreweId)), orderBy('name')),
          ...liveQueryListener('members', emit, fail),
        ),
        apply: (changes) => {
          const roster = rosters.get(subkreweId) ?? new Set<string>()
          rosters.set(subkreweId, roster)
          const subkrewe = this.subkrewes[subkrewePath]
          membersStore.$patch((state) => {
            for (const { path, data } of changes) {
              const member = membersStore.convertToMember(idOf(path), data!, false)
              if (subkrewe) member.subkrewe = subkrewe
              state.members[path] = member
              roster.add(idOf(path))
            }
          })
          this.fillRoster(subkreweId)
        },
        left: (paths) => {
          for (const path of paths) rosters.get(subkreweId)?.delete(idOf(path))
          this.fillRoster(subkreweId)
        },
        evict: paths => membersStore.evictMembers(paths),
        stopped: () => {
          rosters.delete(subkreweId)
          this.fillRoster(subkreweId)
        },
      }
    },

    /** Put a live roster's members on its SubKrewe */
    fillRoster(subkreweId: string) {
      const membersStore = useMembersStore()
      const path = `subkrewes/${subkreweId}`
      const ids = rosters.get(subkreweId)
      this.$patch((state) => {
        const subkrewe = state.subkrewes[path]
        if (!subkrewe) return
        subkrewe.members = [...ids ?? []]
          .map(id => membersStore.members[`members/${id}`])
          .filter((member): member is Member => Boolean(member))
      })
    },

    /**
     * Put live SubKrewes into the store, captains resolved (convertToSubKrewe
     * finds them among the captains, held for as long as the SubKrewe is).
     * The members and captains a SubKrewe already carries are kept: the
     * rosters fill those.
     */
    async applySubkrewes(changes: LiveChange<SubKreweFirestoreData>[]) {
      try {
        await useMembersStore().fetchCaptains()
      }
      catch (error) {
        console.error('[SubKrewes] Error loading captains:', error)
      }
      this.$patch((state) => {
        for (const { path, data } of changes) {
          const subkrewe = this.convertToSubKrewe(path.slice('subkrewes/'.length), data!)
          const existing = state.subkrewes[path]
          if (existing) {
            subkrewe.members = existing.members
            subkrewe.captains = existing.captains?.length ? existing.captains : subkrewe.captains
          }
          state.subkrewes[path] = subkrewe
        }
      })
      for (const { path } of changes) {
        const id = path.slice('subkrewes/'.length)
        if (rosters.has(id)) this.fillRoster(id)
      }
      this.updateSubKrewesArrayState()
    },

    evictSubkrewes(paths: string[]) {
      this.$patch((state) => {
        for (const path of paths) delete state.subkrewes[path]
      })
    },

    /**
     * Update a SubKrewe in Firestore using a complete SubKrewe object
     */
    async updateSubKrewe(subkrewe: SubKrewe, updates: Partial<SubKrewe> = {}): Promise<boolean> {
      try {
        if (!subkrewe || !subkrewe.id) {
          console.error('Cannot update subkrewe: invalid subkrewe object or missing ID')
          return false
        }

        // Create a merged object for Firestore update
        const db = useFirestore()
        const subkreweRef = doc(db, 'subkrewes', subkrewe.id)
        // Sanitize updates before converting to Firestore format
        const sanitizedUpdates = deepTrim({ ...updates })

        // Normalize any website / social media links the caller included, so
        // only absolute http(s) URLs are ever persisted.
        Object.assign(sanitizedUpdates, pickSubKreweLinkFields(sanitizedUpdates as Record<string, unknown>))

        // Translate a legacy-shape `rolling_next_parade` update (single
        // 'Yes'|'No'|'Unknown' scalar emitted by the SubKrewe form / rolling
        // status banner) into the per-parade-year map. We merge into the
        // existing map under the active parade year and also emit a Firestore
        // dotted-path update to avoid overwriting entries for other years.
        let rollingDottedPath: string | null = null
        let rollingDottedValue: 'Yes' | 'No' | 'Unknown' | null = null
        if (
          Object.prototype.hasOwnProperty.call(sanitizedUpdates, 'rolling_next_parade')
          && typeof sanitizedUpdates.rolling_next_parade === 'string'
        ) {
          const value = sanitizedUpdates.rolling_next_parade as 'Yes' | 'No' | 'Unknown'
          const paradeYear = String(useSystemConfigStore().paradeYear)
          const existing = subkrewe.rolling_next_parade_by_year || {}
          sanitizedUpdates.rolling_next_parade_by_year = { ...existing, [paradeYear]: value }
          delete sanitizedUpdates.rolling_next_parade
          rollingDottedPath = `rolling_next_parade_by_year.${paradeYear}`
          rollingDottedValue = value
        }

        // If terms_and_conditions changed, stamp terms_updated_at so prior
        // member acceptances (which capture the previous timestamp) become
        // stale and require re-acceptance.
        if (Object.prototype.hasOwnProperty.call(sanitizedUpdates, 'terms_and_conditions')) {
          const newTerms = (sanitizedUpdates.terms_and_conditions || '') as string
          const oldTerms = (subkrewe.terms_and_conditions || '') as string
          if (newTerms !== oldTerms) {
            sanitizedUpdates.terms_updated_at = serverTimestamp() as unknown as Date
          }
        }

        const firestoreData = this.convertFieldsToFirestore(sanitizedUpdates)

        // When using the dotted-path update, don't also send the whole map
        // (which would overwrite other years).
        if (rollingDottedPath) {
          delete firestoreData.rolling_next_parade_by_year
        }

        // Update the document in Firestore
        await updateDoc(subkreweRef, {
          ...firestoreData,
          ...(rollingDottedPath && rollingDottedValue
            ? { [rollingDottedPath]: rollingDottedValue }
            : {}),
          updated_at: serverTimestamp(),
        })

        // Update local state in a clean way that preserves reactivity
        const path = `subkrewes/${subkrewe.id}`

        // Use Pinia's patch method to update specific fields
        this.$patch((state) => {
          if (state.subkrewes[path]) {
            // Only update the fields that have changed. Record views on both
            // sides: the keys are `string`, so neither typed shape can be
            // indexed with them directly.
            const target = state.subkrewes[path] as unknown as Record<string, unknown>
            const source = sanitizedUpdates as Record<string, unknown>
            Object.keys(source).forEach((key) => {
              if (key !== 'captains' && key !== 'members') {
                target[key] = source[key]
              }
            })

            // Always update timestamp
            state.subkrewes[path].updated_at = new Date()
          }
        })

        return true
      }
      catch (error) {
        console.error(`Error updating subkrewe ${subkrewe.id}:`, error)
        return false
      }
    },

    /**
     * Record an approval decision. Goes through `buildApprovalUpdate()` so
     * bands get their per-parade-year decision and every change is stamped
     * with when and by whom.
     */
    async setApproval(
      subkrewe: SubKrewe,
      status: ApprovalStatus,
      options: { scope: ApprovalScope, actorId?: string | null, reason?: string | null },
    ): Promise<boolean> {
      try {
        if (!subkrewe?.id) {
          console.error('Cannot set approval: invalid subkrewe object or missing ID')
          return false
        }

        const update = buildApprovalUpdate(subkrewe, status, {
          ...options,
          paradeYear: useSystemConfigStore().paradeYear,
          now: new Date(),
        })

        const db = useFirestore()
        await updateDoc(doc(db, 'subkrewes', subkrewe.id), { ...update, updated_at: serverTimestamp() })

        const path = `subkrewes/${subkrewe.id}`
        this.$patch((state) => {
          const target = state.subkrewes[path] as unknown as Record<string, unknown> | undefined
          if (!target) return
          for (const [key, value] of Object.entries(update)) {
            const [field, subKey] = key.split('.')
            if (field && subKey) {
              const existing = target[field] as Record<string, unknown> | undefined
              target[field] = { ...(existing || {}), [subKey]: value }
            }
            else {
              target[key] = value
            }
          }
          target.updated_at = new Date()
        })

        return true
      }
      catch (error) {
        console.error(`Error setting approval for subkrewe ${subkrewe.id}:`, error)
        return false
      }
    },
    convertFieldsToFirestore(data: Partial<SubKrewe>): Partial<SubKreweFirestoreData> {
      const result: Partial<SubKreweFirestoreData> = {}

      Object.entries(data).forEach(([key, value]) => {
        // Skip functions, complex objects and undefined values
        if (
          typeof value !== 'function'
          && value !== undefined
          && key !== 'captains'
          && key !== 'members'
          && !key.startsWith('_')
        ) {
          // Record view: `Object.entries` yields `string` keys, which cannot
          // index the typed Firestore shape.
          ;(result as Record<string, unknown>)[key] = value
        }
      })

      return result
    },
    /**
     * A SubKrewe's members, live while the caller holds them. Fills the
     * SubKrewe's members[] array, a runtime convenience for UI components
     * that show member lists or counts (it isn't stored in the database).
     */
    async loadSubkreweMembers(subkrewe: SubKrewe): Promise<Member[]> {
      if (!subkrewe || !subkrewe.id) {
        console.error('Cannot load members: invalid subkrewe object or missing ID')
        return []
      }
      try {
        await acquireLive(this.rosterSpec(subkrewe.id))
      }
      catch (error) {
        console.error(`Error loading members for subkrewe ${subkrewe.id}:`, error)
        return []
      }
      return this.subkrewes[`subkrewes/${subkrewe.id}`]?.members ?? []
    },

    /**
     * Assign a member as captain of a subkrewe
     * Handles all aspects of the relationship change
     */

    async assignCaptainToSubKrewe(subkrewe: SubKrewe, newCaptain: Member): Promise<boolean> {
      try {
        if (!subkrewe || !newCaptain) {
          console.error('Invalid subkrewe or captain')
          return false
        }

        const membersStore = useMembersStore()

        // Check if they're already a captain in this subkrewe
        if (subkrewe.captains && subkrewe.captains.some(captain => captain.id === newCaptain.id)) {
          return true // Already assigned, nothing to do
        }

        // Set up the member as a captain
        const updatedNewCaptain = {
          ...newCaptain,
          member_type: 'captain',
          subkrewe: subkrewe,
        } as Member

        // Handle old subkrewe relationship if needed
        const oldSubKrewe = newCaptain.subkrewe

        // Get Firestore references
        const db = useFirestore()
        const captainRef = doc(db, 'members', newCaptain.id)
        const subkreweRef = doc(db, 'subkrewes', subkrewe.id)

        // IMPORTANT FIX: Update captain first to satisfy security rules
        // 1. First update the member to be a captain. Only these fields: a
        // captain making a co-captain may change nothing else on another
        // member (firestore.rules).
        await updateDoc(captainRef, {
          member_type: 'captain',
          subkrewe: subkreweRef,
          updated_at: serverTimestamp(),
        })

        // 2. Then update the SubKrewe with the captain reference. Expected
        // captains (names, emails, phones every member can read) are done
        // once a SubKrewe has a captain: signup offers the claim only before
        // then, and firestore.rules honors them only before then.
        // arrayUnion, not a list built from local state: a signup claim runs
        // this twice at once (the claim itself, and ensureCaptainSynchronized
        // from the member snapshot), and the snapshot listeners fill the local
        // captains in between, so the second run wrote the captain twice
        await updateDoc(subkreweRef, {
          captains: arrayUnion(captainRef),
          expected: deleteField(),
          expected_captain: deleteField(),
          updated_at: serverTimestamp(),
        })

        // 3. Only update local state AFTER all Firestore writes succeed
        const subkrewePath = `subkrewes/${subkrewe.id}`
        const captainPath = `members/${newCaptain.id}`

        this.$patch((state) => {
          // Remove from old subkrewe if transferring
          if (oldSubKrewe && oldSubKrewe.id !== subkrewe.id) {
            const oldSubkrewePath = `subkrewes/${oldSubKrewe.id}`
            if (state.subkrewes[oldSubkrewePath]) {
              state.subkrewes[oldSubkrewePath].members
                = state.subkrewes[oldSubkrewePath].members?.filter(m => m.id !== newCaptain.id) || []
              if (newCaptain.member_type === 'captain') {
                state.subkrewes[oldSubkrewePath].captains
                  = state.subkrewes[oldSubkrewePath].captains.filter(c => c.id !== newCaptain.id)
              }
            }
          }

          // Update the target subkrewe
          if (state.subkrewes[subkrewePath]) {
            const sk = state.subkrewes[subkrewePath]
            if (!sk.members) sk.members = []
            sk.members = sk.members.filter(m => m.id !== newCaptain.id)
            sk.members.push(updatedNewCaptain)
            sk.captains = [...(sk.captains || []).filter(c => c.id !== newCaptain.id), updatedNewCaptain]
            sk.expected = undefined
          }
        })

        membersStore.$patch((state) => {
          state.members[captainPath] = updatedNewCaptain
        })

        return true
      }
      catch (error) {
        console.error(`Error assigning captain to subkrewe ${subkrewe?.id}:`, error)
        return false
      }
    },

    /**
     * Remove a captain from a subkrewe
     * Handles all relationship changes
     */
    async removeCaptainFromSubKrewe(subkrewe: SubKrewe, captain: Member, keepAsMember = false): Promise<boolean> {
      try {
        // Verify captain is in the captains array
        if (!subkrewe.captains || !subkrewe.captains.some(c => c.id === captain.id)) {
          console.error('Captain not found in subkrewe captains list')
          return false
        }

        const membersStore = useMembersStore()

        // Update in-memory first for immediate reactivity
        this.$patch((state) => {
          const subkrewePath = `subkrewes/${subkrewe.id}`
          if (state.subkrewes[subkrewePath]) {
            // Remove from captains array
            state.subkrewes[subkrewePath].captains
              = state.subkrewes[subkrewePath].captains.filter(c => c.id !== captain.id)

            // Remove from members array if not keeping as member
            if (!keepAsMember) {
              state.subkrewes[subkrewePath].members
                = state.subkrewes[subkrewePath].members?.filter(m => m.id !== captain.id)
            }
          }
        })

        // Then update Firestore
        const db = useFirestore()
        const subkreweRef = doc(db, 'subkrewes', subkrewe.id)

        // Get current captains excluding the one being removed
        const updatedCaptains = subkrewe.captains.filter(c => c.id !== captain.id)

        // Prepare updates for Firestore with proper typing
        const updates: Partial<SubKreweFirestoreData> = {
          updated_at: serverTimestamp(),
        }

        if (updatedCaptains.length > 0) {
          // Still have some captains left
          updates.captains = updatedCaptains.map(c => doc(db, 'members', c.id))
        }
        else {
          // No captains left
          updates.captains = deleteField()
        }

        // If not keeping as member, also update the members array in Firestore
        if (!keepAsMember) {
          const updatedMembers = subkrewe.members?.filter(m => m.id !== captain.id) || []
          updates.members = updatedMembers.length > 0
            ? updatedMembers.map(m => doc(db, 'members', m.id))
            : deleteField()
        }

        // Cast to any only at the Firestore boundary
        await updateDoc(subkreweRef, updates)

        // Update the captain document
        const captainRef = doc(db, 'members', captain.id)
        await updateDoc(captainRef, {
          member_type: 'member', // Demote back to regular member
          ...(keepAsMember ? {} : { subkrewe: deleteField() }),
          updated_at: serverTimestamp(),
        })

        // Update the captain in memory
        membersStore.$patch((state) => {
          const captainPath = `members/${captain.id}`
          if (state.members[captainPath]) {
            state.members[captainPath].member_type = 'member'
            if (!keepAsMember) {
              state.members[captainPath].subkrewe = null
            }
          }
        })

        return true
      }
      catch (error) {
        console.error(`Error removing captain from subkrewe ${subkrewe.id}:`, error)
        return false
      }
    },

    /**
     * Add a regular member to a subkrewe
     * Simpler than captain assignment
     * @param suppressNotifications If true, no notifications will be sent (useful when called from tasks)
     * @param actingUserId Optional ID of the user performing the action (for notifications)
     */
    async addMemberToSubKrewe(
      subkrewe: SubKrewe,
      member: Member,
      suppressNotifications = false,
      actingUserId?: string,
    ): Promise<boolean> {
      // Cannot add a captain from another subkrewe
      if (member.member_type === 'captain' && member.subkrewe && member.subkrewe.id !== subkrewe.id) {
        console.error('Cannot add a captain from another subkrewe directly')
        return false
      }

      // Use the member store's changeMemberSubKrewe method
      const membersStore = useMembersStore()
      // Takes two arguments — notification handling lives in the callers, so
      // `suppressNotifications` and `actingUserId` are used below, not here.
      const success = await membersStore.changeMemberSubKrewe(member, subkrewe)

      // If successful and notifications aren't suppressed, send notifications to captains
      if (success && !suppressNotifications) {
        try {
          console.warn('Sending notifications for new member joining:', member.id, subkrewe.id)
          const { createMemberJoinedNotificationForCaptains } = useMembershipNotifications()
          const notificationIds = await createMemberJoinedNotificationForCaptains(
            member.id,
            subkrewe.id,
            actingUserId, // Pass the acting user ID to exclude them from notifications
          )
          console.warn('Join notifications sent:', notificationIds)
        }
        catch (err) {
          console.error('Error sending captain notifications for new member:', err)
          // Don't fail the operation if notifications fail
        }
      }

      return success
    },

    /**
     * Remove a member from a subkrewe
     * @param actingUserId Optional ID of the user performing the removal (for notifications)
     * @param reason Optional reason for the removal (e.g., "banned", "removed by captain", "voluntary")
     */
    async removeMemberFromSubKrewe(subkrewe: SubKrewe, member: Member, actingUserId?: string, reason?: string): Promise<boolean> {
      // Cannot remove a captain directly
      if (member.member_type === 'captain' && subkrewe.captains?.some(c => c.id === member.id)) {
        console.error('Cannot remove a captain directly - use removeCaptainFromSubKrewe')
        return false
      }

      // Get the acting user ID (defaults to current user if provided by plugin; avoid hard dependency in tests)
      let actualActingUserId = actingUserId
      if (!actualActingUserId) {
        try {
          // This may warn if VueFireAuth module isn't installed; wrap in try/catch to avoid noisy test output
          const current = useCurrentUser()
          actualActingUserId = current?.value?.uid
        }
        catch {
          // ignore
        }
      }

      // If still no valid user ID, use a system identifier to avoid the warning
      if (!actualActingUserId) {
        console.warn('No acting user ID available for member removal - using system identifier')
        actualActingUserId = 'system-action'
      }

      // Use the member store's method
      const membersStore = useMembersStore()
      const success = await membersStore.changeMemberSubKrewe(member, null)

      if (success) {
        try {
          // 1. Send notifications to captains (except the acting user if they're a captain)
          if (subkrewe.captains && subkrewe.captains.length > 0) {
            // Filter out the acting user from recipients
            const captainsToNotify = subkrewe.captains.filter(c => c.id !== actualActingUserId)

            if (captainsToNotify.length > 0) {
              console.warn('Sending notifications for member leaving (to captains):', member.id, subkrewe.id)
              const { createMemberLeftNotificationForCaptains } = useMembershipNotifications()
              console.warn('Reason for removal:', reason)
              const notificationIds = await createMemberLeftNotificationForCaptains(
                member.id,
                subkrewe.id,
                actualActingUserId, // Pass the acting user ID to exclude them from notifications
                reason, // Pass the reason to the notification
              )
              console.warn('Leave notifications sent to captains:', notificationIds)
            }
            else {
              console.warn('No captains to notify (acting user is the only captain)')
            }
          }

          // 2. Send notification to the member with appropriate tone
          const { createRemovedFromSubKreweNotification, createSelfLeftSubKreweNotification } = useMembershipNotifications()
          let memberNotifId: string
          if (actualActingUserId === member.id) {
            // Self-initiated leave: softer copy
            memberNotifId = await createSelfLeftSubKreweNotification(member.id, subkrewe.name)
          }
          else {
            // Removed by captain/admin/system: explicit removal copy
            memberNotifId = await createRemovedFromSubKreweNotification(
              member.id,
              subkrewe.name,
              actualActingUserId,
              reason,
            )
          }
          console.warn('Member leave/removal notification sent to member:', memberNotifId)

          // 3. Fraud signal to admins if member had a pooled (bulk) ticket for this SubKrewe in the current year
          try {
            // The configured parade year (`nextParadeYear` isn't a runtime
            // config key, so this always checked 2026)
            const year = String(useSystemConfigStore().paradeYear)

            const purchases = (member.purchases ?? {}) as Record<string, unknown>
            const yearPurchases = (purchases[year] ?? {}) as Record<string, unknown>
            const membership = Array.isArray((yearPurchases as { membership?: unknown }).membership)
              ? (yearPurchases as { membership?: unknown[] }).membership as Array<Record<string, unknown>>
              : []
            const hadPooledFromThisSubkrewe = membership.some(it => it?.pooled === true && it?.subkreweId === subkrewe.id)

            // Debug removed

            if (hadPooledFromThisSubkrewe) {
              const { useAdminNotifications } = await import('~/composables/notifications/useAdminNotifications.js')
              const adminNotif = useAdminNotifications()
              const title = 'Pooled member left SubKrewe'
              const body = `${member.name || member.id} left ${subkrewe.name} (${subkrewe.id}) after redeeming a pooled ticket for ${year}.` + (reason ? ` Reason: ${reason}.` : '')
              await adminNotif.createAdminAlertNotification(title, body, { priority: 'high', linkType: 'member', linkTarget: member.id, sendEmail: true })
            }
          }
          catch (e) {
            console.error('Error emitting fraud signal notification:', e)
          }
        }
        catch (err) {
          console.error('Error sending notifications for member leaving:', err)
          // Don't fail the operation if notifications fail
        }
      }

      return success
    },

    updateSubKrewesArrayState() {
      // This is a trick to force Vue's reactivity system to recognize the change
      // by replacing the array reference
      const _temp = [...this.subkrewesArray]
      // The actual action is done in the getter, this just forces a refresh
    },

    // Add these functions to implement SubKrewe creation and captain assignment
    /**
     * Create a new SubKrewe and assign member as captain
     */
    createSubKrewe: async function (subkreweData: Partial<SubKrewe>, member: Member, options?: { assignCaptain?: boolean, autoApproved?: boolean }): Promise<string | null> {
      try {
        const membersStore = useMembersStore()
        const tasksStore = useTasksStore() // Add this line to import the tasks store
        const db = useFirestore()

        // 1. Create the SubKrewe in Firestore first
        const subkrewesRef = collection(db, 'subkrewes')

        const assignCaptain = options?.assignCaptain !== false
        const autoApproved = options?.autoApproved === true

        // Sanitize incoming form data (trim strings) before building Firestore payload
        const sanitized = deepTrim({ ...subkreweData })

        // Translate any legacy-scalar `rolling_next_parade` on the incoming
        // payload into the per-parade-year map so new documents are written in
        // the new shape.
        const paradeYearKey = String(useSystemConfigStore().paradeYear)
        const incomingScalar = typeof sanitized.rolling_next_parade === 'string'
          ? sanitized.rolling_next_parade as 'Yes' | 'No' | 'Unknown'
          : undefined
        const rollingByYear: Record<string, 'Yes' | 'No' | 'Unknown'> = {
          ...(sanitized.rolling_next_parade_by_year || {}),
        }
        if (incomingScalar && !rollingByYear[paradeYearKey]) {
          rollingByYear[paradeYearKey] = incomingScalar
        }
        else if (!incomingScalar && !rollingByYear[paradeYearKey]) {
          rollingByYear[paradeYearKey] = 'Unknown'
        }

        // Format the data for Firestore
        const now = serverTimestamp()
        const initialApproval = sanitized.approved || (autoApproved ? 'Yes' : 'Pending') // Allow override or auto-approve
        const subkreweDocData: Partial<SubKreweFirestoreData> = {
          name: sanitized.name,
          description: sanitized.description || '',
          // Collected by the registration form; without this the address a
          // captain enters at signup was dropped until they re-entered it in
          // the edit form.
          email: sanitized.email || '',
          approved: initialApproval,
          approval_changed_at: now,
          approval_changed_by: member.id,
          // A band created already decided is decided for this parade year too.
          ...(isPerYearApprovalSubkrewe(sanitized) && initialApproval !== 'Pending'
            ? { approval_by_year: { [paradeYearKey]: { status: initialApproval, changed_at: now, changed_by: member.id } } }
            : {}),
          created_at: now,
          updated_at: now,
          estimated_members: ensureNumber(sanitized.estimated_members),
          // Remove secondary field as we're replacing it with captains
          admission_policy: sanitized.admission_policy || 'open',
          admission_restrictions: sanitized.admission_restrictions || '',
          contraption: sanitized.contraption || false,
          contraption_info: sanitized.contraption_info || '',
          music: sanitized.music || 'none',
          music_other: sanitized.music_other || '',
          rolling_next_parade_by_year: rollingByYear,
          visible_in_browser: sanitized.visible_in_browser, // Add this line to save visible_in_browser
          specialty: sanitized.specialty, // Persist specialty when provided
          terms_and_conditions: sanitized.terms_and_conditions || '',
          // Stamp the terms timestamp at creation when terms are supplied.
          // Without it, a later edit writes the *first* terms_updated_at, which
          // memberNeedsToAcceptTerms() has nothing to compare against — so
          // members who accepted the original text would never be asked to
          // re-accept the revision.
          ...(sanitized.terms_and_conditions?.trim() ? { terms_updated_at: now } : {}),
          // Website / social media links, normalized to absolute http(s) URLs
          ...pickSubKreweLinkFields(sanitized as Record<string, unknown>),
          // Set captains array with the first captain
          captains: assignCaptain ? [doc(db, 'members', member.id)] : [],
        }

        // Create a clean object without undefined values. Record views on both
        // sides: `for...in` keys are `string` and cannot index the typed shape.
        const cleanedData: Partial<SubKreweFirestoreData> = {}
        const cleanedTarget = cleanedData as Record<string, unknown>
        const docSource = subkreweDocData as Record<string, unknown>
        for (const key in docSource) {
          if (docSource[key] !== undefined) {
            cleanedTarget[key] = docSource[key]
          }
        }

        // Add the document to Firestore
        const newSubkreweDoc = await addDoc(subkrewesRef, cleanedData)

        // 2. Update the in-memory store
        const subkrewe = this.convertToSubKrewe(newSubkreweDoc.id, cleanedData as SubKreweFirestoreData, false)
        // Initialize the captains array based on options
        subkrewe.captains = assignCaptain ? [member] : []
        this.$patch((state) => {
          state.subkrewes[newSubkreweDoc.path] = subkrewe
        })

        // 3. Optionally update the member using the members store
        if (assignCaptain) {
          const success = await membersStore.updateMember(member, {
            member_type: 'captain',
            subkrewe: subkrewe,
          })

          if (!success) {
            console.error('Failed to update member as captain')
            // Consider rolling back the subkrewe creation?
          }
        }

        // 4. Create an approval task for administrators (best-effort)
        if (!autoApproved) {
          try {
            await tasksStore.createSubKreweApprovalTask(
              newSubkreweDoc.id,
              subkrewe.name,
              member.id,
              member.name,
            )
          }
          catch (taskErr) {
            console.warn('[SubKrewes] Skipping approval task creation due to error:', taskErr)
            // Non-fatal: continue and return created SubKrewe ID
          }
        }

        // 5. Force reactivity update
        this.updateSubKrewesArrayState()

        return newSubkreweDoc.id
      }
      catch (error) {
        console.error('Error creating SubKrewe:', error)
        return null
      }
    },

    /**
     * Find SubKrewes by expected captain email
     */
    /**
 * Find SubKrewes by expected captain email
 * Fix to correctly handle the nested array of objects structure
 */
    async findSubkrewesByExpectedCaptainEmail(email: string): Promise<SubKrewe[]> {
      try {
        const db = useFirestore()
        const lowerCaseEmail = email.toLowerCase().trim()

        // First, we need to fetch all subkrewes without captains assigned
        // since we can't directly query the nested array structure
        const subkreweCollection = collection(db, 'subkrewes')
        const querySnapshot = await getDocs(subkreweCollection)

        const results: SubKrewe[] = []

        // Then manually filter by checking the expected.captains array for each subkrewe
        querySnapshot.forEach((doc) => {
          const data = doc.data() as SubKreweFirestoreData
          const subkrewe = this.convertToSubKrewe(doc.id, data)
          // Skip subkrewes that already have captains assigned
          if (data.captains && Array.isArray(data.captains) && data.captains.length > 0) {
            return
          }

          // Check if this subkrewe has an expected captain with matching email
          const hasMatchingCaptain = subkrewe.expected?.captains?.some(captain =>
            captain.email.toLowerCase() === lowerCaseEmail,
          )

          if (hasMatchingCaptain) {
            results.push(subkrewe)
          }
        })

        return results
      }
      catch (error) {
        console.error('Error finding SubKrewes by expected captain email:', error)
        return []
      }
    },

    /**
   * Update a SubKrewe's captain
   */
    async updateSubKreweCaptain(
      subkreweId: string,
      memberId: string,
      expectedEmail?: string,
      skipFetch = false,
    ): Promise<boolean> {
      try {
        const membersStore = useMembersStore()

        // 1. Validate the SubKrewe
        let subkrewe: SubKrewe | null

        if (skipFetch && this.subkrewes[`subkrewes/${subkreweId}`]) {
          // Use the cached subkrewe if available
          subkrewe = this.subkrewes[`subkrewes/${subkreweId}`] || null
        }
        else {
          // Otherwise fetch it from Firestore
          subkrewe = await this.fetchSubKrewe(subkreweId)
        }

        if (!subkrewe) {
          console.warn('SubKrewe not found')
          return false
        }

        // 2. If expectedEmail is provided, verify it matches one of the expected captains
        if (expectedEmail && subkrewe.expected && subkrewe.expected.captains) {
          const expectedCaptain = subkrewe.expected.captains.find(
            captain => captain.email === expectedEmail,
          )

          if (!expectedCaptain) {
            console.error('Expected email does not match any expected captain')
            return false
          }
        }

        // 3. Fetch the member
        let member: Member | null

        const cachedMember = membersStore.members[`members/${memberId}`]
        if (skipFetch && cachedMember) {
          // Use the cached member if available
          member = cachedMember
        }
        else {
          // Otherwise fetch from Firestore
          member = await membersStore.fetchMember(memberId)
        }

        if (!member) {
          console.warn('Member not found')
          return false
        }

        // 4. Use assignCaptainToSubKrewe to update all relationships properly
        return await this.assignCaptainToSubKrewe(subkrewe, member)
      }
      catch (error) {
        console.error('Error updating SubKrewe captain:', error)
        return false
      }
    },
    /**
    * Ensure a member who is a captain is properly synchronized as the captain of their subkrewe
    */
    async ensureCaptainSynchronized(subkrewe: SubKrewe, captainMember: Member): Promise<boolean> {
      try {
        // If the member isn't actually a captain, nothing to do
        if (captainMember.member_type !== 'captain') {
          console.error('Member is not a captain')
          return false
        }

        // Check if the captain is already in the list
        const isCaptainInList = subkrewe.captains
          && subkrewe.captains.some(c => c.id === captainMember.id)

        // If already synchronized, nothing to do
        if (isCaptainInList) {
          return true
        }

        // Add this captain to the subkrewe's captains list
        return await this.assignCaptainToSubKrewe(subkrewe, captainMember)
      }
      catch (error) {
        console.error('Error ensuring captain synchronized:', error)
        return false
      }
    },

    /**
     * Delete a SubKrewe and update all member relationships
     * This also sends notifications to all members
     * @param subkreweId The ID of the SubKrewe to delete
     * @param reason Optional reason for deletion to show in the notification
     * @returns Promise<boolean> indicating success
     */
    async deleteSubKrewe(subkreweId: string, actingUserId: string, reason?: string): Promise<boolean> {
      try {
        // 1. Get the SubKrewe to check if it exists
        const subkrewe = await this.fetchSubKrewe(subkreweId)
        if (!subkrewe) {
          console.error(`Cannot delete SubKrewe: SubKrewe with ID ${subkreweId} not found`)
          return false
        }

        // 2. Get all members of this SubKrewe to notify them
        await this.loadSubkreweMembers(subkrewe)
        const membersToNotify = [...(subkrewe.members || []), ...(subkrewe.captains || [])]

        // 3. Get the acting user's info for the notification
        const membersStore = useMembersStore()
        const actingMember = await membersStore.fetchMember(actingUserId)
        if (!actingMember) {
          console.error(`Cannot find acting member with ID ${actingUserId}`)
          return false
        }

        // 4. Send notifications to all members before deleting
        const { createRemovedFromSubKreweNotification } = useMembershipNotifications()
        const notificationPromises = membersToNotify.map(member =>
          createRemovedFromSubKreweNotification(
            member.id,
            subkrewe.name,
            actingUserId,
            `SubKrewe was deleted by administrator${reason ? `: ${reason}` : '.'}`,
          ),
        )

        await Promise.all(notificationPromises)

        // 5. Update all members to remove their SubKrewe reference
        const memberUpdatePromises = membersToNotify.map((member) => {
          if (member.member_type === 'captain') {
            // Demote captains to regular members
            return membersStore.updateMember(member, {
              member_type: 'member',
              subkrewe: null,
            })
          }
          else {
            // Just remove the SubKrewe reference for regular members
            return membersStore.updateMember(member, {
              subkrewe: null,
            })
          }
        })

        await Promise.all(memberUpdatePromises)

        // 6. Delete the actual SubKrewe document
        const db = useFirestore()
        const subkreweRef = doc(db, 'subkrewes', subkreweId)
        await deleteDoc(subkreweRef)

        // 7. Remove from local state
        this.$patch((state) => {
          const path = `subkrewes/${subkreweId}`
          delete state.subkrewes[path]
        })

        return true
      }
      catch (error) {
        console.error(`Error deleting subkrewe ${subkreweId}:`, error)
        return false
      }
    },

    /**
     * Abandon a SubKrewe as a captain
     * This sets the SubKrewe as abandoned but does not delete it
     * @param subkrewe The SubKrewe to abandon
     * @param actingUserId ID of the captain abandoning the SubKrewe
     * @param reason Optional reason for abandonment
     * @returns Promise<boolean> indicating success
     */
    async abandonSubKrewe(
      subkrewe: SubKrewe,
      actingUserId: string,
      reason?: string,
    ): Promise<boolean> {
      try {
        if (!subkrewe || !subkrewe.id) {
          console.error('Cannot abandon SubKrewe: invalid SubKrewe object')
          return false
        }

        // Verify the user is a captain of this SubKrewe
        const membersStore = useMembersStore()
        const captain = await membersStore.fetchMember(actingUserId)

        if (!captain || captain.member_type !== 'captain'
          || !captain.subkrewe || captain.subkrewe.id !== subkrewe.id) {
          console.error('User is not a captain of this SubKrewe')
          return false
        }

        // Verify this is the only captain and there are no other members
        if (subkrewe.captains && subkrewe.captains.length > 1) {
          console.error('Cannot abandon SubKrewe with multiple captains')
          return false
        }

        // Ensure members are loaded
        await this.loadSubkreweMembers(subkrewe)

        // Check if there are other members besides the captain
        if (subkrewe.members && subkrewe.members.length > 1) {
          console.error('Cannot abandon SubKrewe with active members')
          return false
        }

        // Get Firestore references
        const db = useFirestore()
        const subkreweRef = doc(db, 'subkrewes', subkrewe.id)

        // 1. Mark the SubKrewe as abandoned and explicitly clear captains array
        await updateDoc(subkreweRef, {
          abandoned: true,
          abandoned_at: serverTimestamp(),
          abandoned_by: actingUserId,
          abandonment_reason: reason || 'No reason provided',
          captains: deleteField(), // Explicitly remove captains array
          updated_at: serverTimestamp(),
        })

        // 2. Demote the captain
        await membersStore.updateMember(captain, {
          member_type: 'member',
          subkrewe: null,
        })

        // 3. Update local state
        this.$patch((state) => {
          const path = `subkrewes/${subkrewe.id}`
          if (state.subkrewes[path]) {
            state.subkrewes[path] = {
              ...state.subkrewes[path],
              abandoned: true,
              abandoned_at: new Date(),
              abandoned_by: actingUserId,
              abandonment_reason: reason || 'No reason provided',
              updated_at: new Date(),
              captains: [], // Clear captains array
              members: [], // Clear members array
            }
          }
        })

        return true
      }
      catch (error) {
        console.error(`Error abandoning SubKrewe ${subkrewe.id}:`, error)
        return false
      }
    },

    /**
    * Hold the SubKrewes relevant to the current user live: every SubKrewe
    * for admins, otherwise the member's own (or `subkreweId`)
    * @returns The release, or null when there's nothing to hold
    */
    subscribeToUserSubKrewes(subkreweId?: string): (() => void) | null {
      const user = useCurrentUser().value
      if (!user?.uid) {
        console.warn('[SubKrewes] Cannot subscribe: No authenticated user')
        return null
      }
      const currentMember = useMembersStore().members[`members/${user.uid}`]
      if (!currentMember) {
        console.warn('[SubKrewes] Cannot subscribe: Member not found in store')
        return null
      }
      if (memberIsAdmin(currentMember)) {
        return holdLive(useAdminLiveStore().subkrewesSpec()).release
      }
      const targetId = subkreweId || currentMember.subkrewe?.id
      return targetId ? this.subscribeToSubKrewe(targetId) : null
    },

    /**
    * Hold a SubKrewe live
    * @returns The release
    */
    subscribeToSubKrewe(subkreweId: string): (() => void) | null {
      if (!subkreweId) {
        console.error('[SubKrewes] Cannot subscribe: Invalid subkreweId')
        return null
      }
      return holdLive(this.subkreweSpec(subkreweId)).release
    },

    /**
    * Hold a SubKrewe's members live (its captains and admins)
    * @returns The release
    */
    subscribeToSubKreweMembers(subkreweId: string): (() => void) | null {
      if (!subkreweId) return null
      const user = useCurrentUser().value
      if (!user?.uid) return null

      const currentMember = useMembersStore().members[`members/${user.uid}`]
      const isAdminOrCaptain = currentMember
        && (memberIsAdmin(currentMember)
          || (currentMember.member_type === 'captain'
            && currentMember.subkrewe?.id === subkreweId))
      if (!isAdminOrCaptain) {
        // A member sees only their captains, whose records they can't read
        // (firestore.rules): the SubKrewe's captains come from the server
        // (fetchCaptains), so there's nothing to watch
        return () => {}
      }
      return holdLive(this.rosterSpec(subkreweId)).release
    },

    /**
     * The browsable SubKrewes, live while the caller holds them (the
     * SubKrewe browser holds them while it's open)
     */
    ensureAvailableSubKrewesSubscription(): void {
      acquireLive(this.availableSpec()).catch((error) => {
        console.error('[SubKrewes] Error in available subscription:', error)
      })
    },

    /**
    * Hold the browsable SubKrewes live
    * @returns The release
    */
    subscribeToAvailableSubKrewes(): (() => void) | null {
      if (!useCurrentUser().value?.uid) {
        console.warn('[SubKrewesStore] Cannot subscribe to available SubKrewes: No authenticated user')
        return null
      }
      return holdLive(this.availableSpec()).release
    },
  },
},
)

function ensureNumber(value: number | string | undefined): number {
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10)
    return isNaN(parsed) ? 0 : parsed
  }
  else if (typeof value === 'number') {
    return value
  }
  return 0
}
